/**
 * Approved ("active") Plans of Record for the current workspace.
 *
 * Backs the top-level PoR mode switch: the tab renders only when at least one
 * approved PoR exists, and the instrument offers a switcher when there are
 * several. Primary is sorted first; when none is marked, most recently updated
 * approved wins (same fallback as resolvePrimaryPor).
 *
 * Module-level cache (short TTL) because the mode switcher renders in both
 * shells on every page — one query per workspace, not per navigation.
 */

import { useEffect, useState } from 'react';
import { listPors } from '../lib/planOfRecordService';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import type { PorRecord } from '../types/plan-of-record';
import { sortPorsForDisplay } from '../types/plan-of-record';

const TTL_MS = 30_000;

let cache: { key: string | null; at: number; pors: PorRecord[] } | null = null;
let inflight: { key: string | null; promise: Promise<PorRecord[]> } | null = null;

export function invalidateActivePorsCache(): void {
  cache = null;
  inflight = null;
}

async function fetchApproved(key: string | null): Promise<PorRecord[]> {
  const { data } = await listPors();
  const pors = sortPorsForDisplay(data.filter((p) => p.status === 'approved'));
  cache = { key, at: Date.now(), pors };
  return pors;
}

export function useActivePors(): PorRecord[] {
  const key = getCurrentWorkspaceId();
  const [pors, setPors] = useState<PorRecord[]>(() =>
    cache && cache.key === key ? cache.pors : [],
  );

  useEffect(() => {
    let alive = true;
    if (cache && cache.key === key && Date.now() - cache.at < TTL_MS) {
      setPors(cache.pors);
      return;
    }
    if (!inflight || inflight.key !== key) {
      const promise = fetchApproved(key).finally(() => {
        if (inflight?.promise === promise) inflight = null;
      });
      inflight = { key, promise };
    }
    void inflight.promise.then((p) => {
      if (alive) setPors(p);
    });
    return () => {
      alive = false;
    };
  }, [key]);

  return pors;
}
